import { useState } from "react";
import { Check, ClipboardCheck, Info } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { withOptimisticOverride } from "@/lib/checklistOptimistic";
import {
  SHARE360_NEXT_STEPS_HEADER,
  SHARE360_NEXT_STEPS_SUBTEXT,
  SHARE360_BUSINESS_CHECKLIST_LABELS,
  SHARE360_BUSINESS_PORTAL_STEPS_LABEL,
  SHARE360_BUSINESS_CALLOUT,
  SHARE360_BUSINESS_ACK_LABEL,
  SHARE360_NONPROFIT_CHECKLIST_LABELS,
  SHARE360_NONPROFIT_PORTAL_STEPS_LABEL,
  SHARE360_NONPROFIT_MARKETING_COLLATERAL_LABEL,
  SHARE360_NONPROFIT_CALLOUT,
  SHARE360_NONPROFIT_ACK_LABEL,
} from "@shared/consultationEligibility";

type StepKey =
  | "clientContacted" | "clientUnderstandsOpportunity" | "proposalUnderstandingAck"
  | "nonprofitContacted" | "launchMeetingExpectationSet" | "rolloutInviteesEncouraged" | "noCostUnderstood"
  | "marketingCollateralDelivered"
  | "associateResponsibilityAck";

// ─── Share360 Business/Nonprofit - "Associate Next Steps" checklist ───────
// Additive to (never a replacement for) the pre-existing Steps-1&2
// consultation gate: all of it must be true - associate attestations here AND
// the Steps-1&2 (Business) or portal-docs (Nonprofit) requirements - before
// consultation.checkEligibility reports ready.
//
// Renders its full container (header, subtext, numbered checklist, callout,
// acknowledgement) IMMEDIATELY from the shared client-approved copy in
// shared/consultationEligibility.ts - it does not wait on
// consultation.checkEligibility to know what to show, only to know each
// item's checked state. This intentionally fixes a real bug (2026-08-18):
// the card previously returned null until that query resolved, so it would
// visibly pop into existence after a delay when a client card opened. Every
// row defaults to unchecked (never a false "complete" state) until the
// server confirms otherwise.
//
// The automated item (item 3 - portal Steps 1&2 for Business, portal
// docs+questionnaire for Nonprofit) is rendered as a plain non-interactive
// row - never a checkbox, never wired to any onClick - because it is not
// part of this mutation's input schema at all server-side; there is no code
// path by which a click here could change it, by design.
export function Share360NextStepsChecklist({ leadId, isNonprofit, readOnly = false, canManageInternalSteps = false }: {
  leadId: number;
  isNonprofit: boolean;
  // Not currently set by any caller - the owning rep, a real admin, and any
  // in-scope Team Portal member (any role, vertical-scoped server-side) can
  // all write this checklist. Kept as an escape hatch for a genuinely
  // view-only caller in the future rather than removed outright.
  readOnly?: boolean;
  /** Admin and in-scope internal Team Portal users only. The server re-checks this permission. */
  canManageInternalSteps?: boolean;
}) {
  const utils = trpc.useUtils();
  const eligibilityQuery = trpc.consultation.checkEligibility.useQuery({ leadId });
  const [optimistic, setOptimistic] = useState<Partial<Record<StepKey, boolean>> | null>(null);
  // Server-confirmed steps from the mutation's own response - once set, this
  // (not the checkEligibility-derived serverSteps below) is the baseline. See
  // the matching comment in Legacy360PreAppointmentChecklist.tsx: waiting on
  // an invalidated refetch before clearing the optimistic overlay caused a
  // real, reproduced bug (2026-08-17) where a checkbox would show the new
  // state for ~1 second then revert, because the refetch it was waiting on
  // could race the mutation and resolve with pre-write data. Trusting the
  // mutation's own response instead removes the race entirely.
  const [confirmed, setConfirmed] = useState<Partial<Record<StepKey, boolean>> | null>(null);

  const setNextSteps = trpc.consultation.setShare360NextSteps.useMutation({
    onSuccess: (data) => {
      setConfirmed(data.steps as Partial<Record<StepKey, boolean>>);
      setOptimistic(null);
      // Background refresh for every OTHER view of this lead - not awaited,
      // since this component's own display no longer depends on it.
      utils.consultation.checkEligibility.invalidate({ leadId });
      utils.lead.list.invalidate();
    },
    onError: (e: any) => {
      // Never keep showing a value that was never actually saved.
      setOptimistic(null);
      toast.error(e.message || "Failed to save this item.");
    },
  });

  const setMarketingCollateral = trpc.consultation.setShare360NonprofitMarketingCollateral.useMutation({
    onSuccess: (data) => {
      setConfirmed(data.steps as Partial<Record<StepKey, boolean>>);
      setOptimistic(null);
      utils.consultation.checkEligibility.invalidate({ leadId });
      utils.lead.list.invalidate();
    },
    onError: (e: any) => {
      setOptimistic(null);
      toast.error(e.message || "Failed to save this item.");
    },
  });

  // Static, client-approved copy - known immediately from the lead's
  // vertical, never waits on the server round trip.
  const before = isNonprofit
    ? (["nonprofitContacted", "launchMeetingExpectationSet", "rolloutInviteesEncouraged", "noCostUnderstood"] as const).map(key => ({ key, label: SHARE360_NONPROFIT_CHECKLIST_LABELS[key] }))
    : (["clientContacted", "clientUnderstandsOpportunity"] as const).map(key => ({ key, label: SHARE360_BUSINESS_CHECKLIST_LABELS[key] }));
  const after = isNonprofit
    ? [{ key: "marketingCollateralDelivered" as const, label: SHARE360_NONPROFIT_MARKETING_COLLATERAL_LABEL }]
    : [{ key: "proposalUnderstandingAck" as const, label: SHARE360_BUSINESS_CHECKLIST_LABELS.proposalUnderstandingAck }];
  const automatedLabel = isNonprofit ? SHARE360_NONPROFIT_PORTAL_STEPS_LABEL : SHARE360_BUSINESS_PORTAL_STEPS_LABEL;
  const callout = isNonprofit ? SHARE360_NONPROFIT_CALLOUT : SHARE360_BUSINESS_CALLOUT;
  const ackLabel = isNonprofit ? SHARE360_NONPROFIT_ACK_LABEL : SHARE360_BUSINESS_ACK_LABEL;

  // Live done-state layer - undefined/still-loading resolves every row to
  // unchecked (never a false "complete" state) until the server confirms
  // otherwise.
  const nextSteps = eligibilityQuery.data?.nextSteps;
  const serverDone: Partial<Record<StepKey, boolean>> = nextSteps
    ? Object.fromEntries([...nextSteps.checklistBeforePortal, ...nextSteps.checklistAfterPortal, nextSteps.ack].map(s => [s.key, s.done]))
    : {};
  const automatedComplete = nextSteps?.automatedComplete ?? false;

  const baseline = confirmed ?? serverDone;
  const displayed = withOptimisticOverride<Record<StepKey, boolean>>(baseline, optimistic);
  const saving = setNextSteps.isPending || setMarketingCollateral.isPending;
  const disabled = readOnly || saving || eligibilityQuery.isLoading;
  const allDone = [...before, ...after].every(s => !!displayed[s.key]) && automatedComplete && !!displayed.associateResponsibilityAck;

  const toggle = (key: StepKey) => {
    if (disabled) return;
    if (key === "marketingCollateralDelivered" && !canManageInternalSteps) return;
    const next = !displayed[key];
    setOptimistic({ ...displayed, [key]: next });
    if (key === "marketingCollateralDelivered") {
      setMarketingCollateral.mutate({ leadId, complete: next });
    } else {
      setNextSteps.mutate({ leadId, [key]: next } as any);
    }
  };

  const renderCheckbox = (key: StepKey, label: string) => {
    const checked = !!displayed[key];
    const internalReadOnly = key === "marketingCollateralDelivered" && !canManageInternalSteps;
    const itemDisabled = disabled || internalReadOnly;
    return (
      <label key={key} className={`flex items-start gap-2 text-[12px] select-none ${itemDisabled ? "cursor-default" : "cursor-pointer"}`}>
        <span
          onClick={(e) => {
            e.preventDefault();
            toggle(key);
          }}
          className="flex-shrink-0 mt-[1px] w-4 h-4 rounded-[4px] border flex items-center justify-center transition-colors"
          style={{
            background: checked ? "#2F7A4D" : "#fff",
            borderColor: checked ? "#2F7A4D" : "#D8D0C2",
            opacity: itemDisabled ? 0.75 : 1,
          }}
        >
          {checked && <Check className="w-3 h-3 text-white" />}
        </span>
        <span className={checked ? "text-[#4A5A4E]" : "text-[#6E665A]"}>
          {label}
          {key === "marketingCollateralDelivered" && (
            <span className="ml-1 text-[10px] font-semibold text-[#8A8071]">(Internal team)</span>
          )}
        </span>
      </label>
    );
  };

  return (
    <div className="rounded-[10px] border p-3" style={{ background: allDone ? "#F0F9F4" : "#FFF9EC", borderColor: allDone ? "#CDE9D9" : "#F0DFB0" }}>
      <div className="flex items-center gap-1.5 text-[12.5px] font-semibold mb-0.5" style={{ color: allDone ? "#2F7A4D" : "#8A6B1F" }}>
        <ClipboardCheck className="w-3.5 h-3.5" />
        {SHARE360_NEXT_STEPS_HEADER}{allDone ? " - Complete" : ""}{readOnly ? " (View Only)" : ""}
      </div>
      <div className="text-[11px] text-[#8A8071] mb-2">{SHARE360_NEXT_STEPS_SUBTEXT}</div>

      <div className="flex flex-col gap-1.5">
        {before.map(item => renderCheckbox(item.key, item.label))}

        {/* System-controlled, read-only - never a checkbox, never clickable. */}
        <div className="flex items-start gap-2 text-[12px]">
          <span
            className="flex-shrink-0 mt-[1px] w-4 h-4 rounded-[4px] border flex items-center justify-center"
            style={{
              background: automatedComplete ? "#2F7A4D" : "#fff",
              borderColor: automatedComplete ? "#2F7A4D" : "#D8D0C2",
              opacity: 0.85,
            }}
          >
            {automatedComplete && <Check className="w-3 h-3 text-white" />}
          </span>
          <span className={automatedComplete ? "text-[#4A5A4E]" : "text-[#6E665A]"}>{automatedLabel}</span>
        </div>

        {after.map(item => renderCheckbox(item.key, item.label))}
      </div>

      {/* Informational callout - not a checkbox, not a gate item. */}
      <div className="mt-3 flex items-start gap-1.5 text-[11px] text-[#6E665A] bg-white/60 border border-[#EBE2D4] rounded-[8px] p-2">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" style={{ color: allDone ? "#2F7A4D" : "#8A6B1F" }} />
        <span>{callout}</span>
      </div>

      {/* Closing acknowledgement - separate from the numbered checklist above. */}
      <div className="mt-2 pt-2 border-t" style={{ borderColor: allDone ? "#CDE9D9" : "#F0DFB0" }}>
        {renderCheckbox("associateResponsibilityAck", ackLabel)}
      </div>
    </div>
  );
}
